import type { ParseKeys } from "i18next";
import { schedulePresets } from "../schedule-presets/schedule-presets";
import type { ScheduleEvent } from "./history-events";
import type { StreamSlot } from "../schedule-document/schedule-values";
import { slotHistoryFields } from "../schedule-document/slot-fields";

export type EventLabel = { key: ParseKeys; params: Record<string, string | number> };

// A slot key is "yyyy-MM-dd-1", so the day and the slot number read straight off it.
function slotParams(slotKey: string, formatDay: (iso: string) => string) {
  const day = formatDay(slotKey.slice(0, 10));
  const number = Number(slotKey.slice(11));
  return { day, number };
}

// A title edit also flips `active`, so the most specific field wins the label.
function slotFieldKey(patch: Partial<StreamSlot>) {
  const field = slotHistoryFields.find(field => field.key in patch);
  if (!field) return "history.slotTitle";
  if (field.key !== "active") return field.historyKey;
  return patch.active ? "history.slotActive" : "history.slotInactive";
}

export function describeEvent(event: ScheduleEvent, formatDay: (iso: string) => string): EventLabel {
  switch (event.type) {
    case "slot/set":
      return { key: slotFieldKey(event.patch), params: slotParams(event.key, formatDay) };
    case "slot/reset":
      return { key: "history.slotReset", params: slotParams(event.key, formatDay) };
    case "slot/swap": {
      const source = slotParams(event.sourceKey, formatDay);
      const target = slotParams(event.targetKey, formatDay);
      return { key: "history.slotSwap", params: { day: source.day, number: source.number, targetDay: target.day, targetNumber: target.number } };
    }
    case "offline/set": {
      const day = formatDay(event.dateKey);
      const { message, active } = event.patch;
      if (message === undefined) return { key: active ? "history.offlineActive" : "history.offlineInactive", params: { day } };
      const isCleared = message.length === 0;
      return { key: isCleared ? "history.offlineClear" : "history.offlineSet", params: { day } };
    }
    case "offline/swap":
      return { key: "history.offlineSwap", params: { day: formatDay(event.sourceDate), targetDay: formatDay(event.targetDate) } };
    case "date/set":
      return { key: "history.weekSet", params: { day: formatDay(event.date) } };
    case "timezone/set":
      return { key: "history.timezoneSet", params: { timezone: event.timezone } };
    case "preset/select": {
      const preset = schedulePresets.find(entry => entry.id === event.presetId);
      return { key: "history.presetSelect", params: { preset: preset?.label ?? event.presetId } };
    }
    case "document/load":
      return { key: "history.documentLoad", params: {} };
    case "document/reset":
      return { key: "history.documentReset", params: {} };
    case "common/set":
      return { key: "history.memberOnlyLabel", params: {} };
    case "profile/set":
      return { key: "history.profileHandle", params: {} };
    case "handle/setPosition":
      return { key: "history.handleMove", params: {} };
    case "handle/setScale":
      return { key: "history.handleScale", params: {} };
    case "handle/setRotation":
      return { key: "history.handleRotation", params: {} };
    case "art/addImages":
      return { key: "history.artImage", params: {} };
    case "art/setTransform":
      return { key: "history.artTransform", params: {} };
    case "art/setVisibility":
      return { key: event.visible ? "history.artShow" : "history.artHide", params: {} };
    case "art/remove":
      return { key: "history.artRemove", params: {} };
    case "art/reorder":
      return { key: "history.artReorder", params: {} };
  }
}
